import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

// One connectivity source shared by mounted Discovery loaders. Native and web
// sources are mutually exclusive so a WebView's online event cannot double-fire.
const subscribers = new Set();
let stopListening;

const startListening = () => {
    let stopped = false;
    let connected;
    let revision = 0;
    let nativeHandle;
    let removeWebListeners;
    const native = Capacitor.isNativePlatform();

    const update = (next) => {
        if (stopped) return;
        revision += 1;
        const reconnected = connected === false && next === true;
        connected = next;
        if (reconnected && (native || window.matchMedia?.('(max-width: 991.98px)').matches)) {
            [...subscribers].forEach((notify) => notify());
        }
    };

    const startWeb = () => {
        if (stopped) return;
        connected = navigator.onLine;
        const online = () => update(true);
        const offline = () => update(false);
        window.addEventListener('online', online);
        window.addEventListener('offline', offline);
        removeWebListeners = () => {
            window.removeEventListener('online', online);
            window.removeEventListener('offline', offline);
        };
    };

    if (native) {
        // Register first, then sample. An event that arrives during getStatus
        // takes precedence over its potentially older result.
        (async () => {
            try {
                const handle = await Network.addListener('networkStatusChange', ({ connected: next }) => update(next));
                if (stopped) {
                    await handle.remove();
                    return;
                }
                nativeHandle = handle;
                const initialRevision = revision;
                const status = await Network.getStatus();
                if (!stopped && revision === initialRevision) update(status.connected);
            } catch (error) {
                if (stopped) return;
                if (nativeHandle) {
                    await nativeHandle.remove().catch(() => {});
                    nativeHandle = null;
                }
                startWeb();
            }
        })();
    } else {
        startWeb();
    }

    return () => {
        stopped = true;
        removeWebListeners?.();
        nativeHandle?.remove().catch(() => {});
    };
};

export const subscribeDiscoveryReconnect = (notify) => {
    subscribers.add(notify);
    if (subscribers.size === 1) stopListening = startListening();
    return () => {
        subscribers.delete(notify);
        if (subscribers.size === 0) {
            stopListening?.();
            stopListening = undefined;
        }
    };
};
